/**
 * Benefit definitions for Critical Illness
 *
 * Defines the structure, labels, and cell specs for each Critical Illness benefit field.
 * Uses shared cell specs and leverages builder patterns where applicable.
 *
 * Field ordering matches the Excel specification exactly.
 */

import {
  addNotDisclosed,
  BaseBenefitPlanPolicyPeriod,
  CriticalIllnessEmployeeBenefitDescriptionTypeEnum,
  CriticalIllnessEmployeeMaximumTypeEnum,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  NotDisclosed,
  NotOffered,
  RateGuaranteeTypeEnum,
  RfpAgeBasisEnum,
  RfpAgeReductionScheduleType,
  RfpCriticalIllnessEmployeeGuaranteeIssueTypeEnum,
  RfpCriticalIllnessSpouseBenefitDescriptionTypeEnum,
  RfpCriticalIllnessSpouseMaximumTypeEnum,
  RfpIllnessTypeEnum,
  RfpInclusionStatusEnum,
  RfpMaximumPayoutTypeEnum,
  RfpOccurrenceTypeEnum,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  buildAgeReductionScheduleBenefitPair,
  buildCommissionBenefits,
  buildPreExistingConditionBenefitPair,
  buildTypeAmountBenefitPair,
  buildTypeAmountWithVariantsBenefit,
} from "../shared/definitions/builders";

import {
  buildEmployeeBenefitFields,
  buildMpOnlyConditionRider,
  configureDependentBenefitFields,
} from "./utils";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Current carrier name",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  ...buildEmployeeBenefitFields({
    benefitDescriptionTypeEnumValues: addNotDisclosed([
      ...Object.values(
        CriticalIllnessEmployeeBenefitDescriptionTypeEnum,
      ).filter((value) => value !== "multipleOptions"),
      "multipleDollarAmounts", // replace multipleOptions with multipleDollarAmounts for FE
    ]),
    benefitMaximumTypeEnumValues: extendEnumWithNotDisclosed(
      CriticalIllnessEmployeeMaximumTypeEnum,
    ),
    guaranteeIssueTypeEnumValues: extendEnumWithNotDisclosed(
      RfpCriticalIllnessEmployeeGuaranteeIssueTypeEnum,
    ),
    benefitDescriptionTypeRequired: true,
    benefitMaximumTypeRequired: true,
    guaranteeIssueTypeRequired: true,
  }),
  dependentBenefitsIncluded: {
    label: "Dependent Benefits Included?",
    description: "Whether dependent (spouse/child) benefits are included",
    cell: sharedCellSpecs.benefitsIncluded,
    networkDependent: false,
    refreshesCells: [
      // Spouse - Benefit Description
      "spouseBenefitDescriptionType",
      "spouseBenefitDescriptionAmount",
      "spouseBenefitDescriptionMinimum",
      // Spouse - Benefit Maximum
      "spouseBenefitMaximumType",
      "spouseBenefitMaximumDollarAmount",
      "spouseBenefitMaximumPercent",
      // Spouse - Guarantee Issue
      "spouseGuaranteeIssueType",
      "spouseGuaranteeIssueAmount",
      // Child - Benefit Description
      "childBenefitDescriptionType",
      "childBenefitDescriptionAmount",
      "childBenefitDescriptionMinimum",
    ],
    isRequired: true,
  },
  ...configureDependentBenefitFields(
    [
      {
        spouseBenefitDescriptionType: {
          label: "Benefit Description - Spouse",
          description: "Type of spouse benefit description",
          cell: {
            ...sharedCellSpecs.enumDropdown,
            cellEditorParams: {
              ...sharedCellSpecs.enumDropdown.cellEditorParams,
              values: addNotDisclosed([
                ...Object.values(
                  RfpCriticalIllnessSpouseBenefitDescriptionTypeEnum,
                ).map((value) =>
                  value === "multipleOptions" ? "multipleDollarAmounts" : value,
                ), // replace multipleOptions with multipleDollarAmounts for FE
              ]),
            },
          },
          networkDependent: false,
          isRequired: true,
          refreshesCells: [
            "spouseBenefitDescriptionAmount",
            "spouseBenefitDescriptionMinimum",
          ],
        },
        spouseBenefitDescriptionAmount: {
          label: "Amount",
          description: "Spouse benefit amount",
          cell: sharedCellSpecs.dollarAmount,
          networkDependent: false,
          nested: true,
          editableWhen: {
            dependsOn: "spouseBenefitDescriptionType",
            disabledValues: ["notIncluded", NotDisclosed],
          },
          cellVariants: {
            dependsOn: "spouseBenefitDescriptionType",
            options: {
              percentage: sharedCellSpecs.percentageAmount,
              multipleOptions: {
                ...sharedCellSpecs.multipleDollarAmountsPopover,
                cellEditorParams: {
                  ...sharedCellSpecs.multipleDollarAmountsPopover
                    .cellEditorParams,
                  maxEntries: 10,
                },
              },
              multipleDollarAmounts: {
                ...sharedCellSpecs.multipleDollarAmountsPopover,
                cellEditorParams: {
                  ...sharedCellSpecs.multipleDollarAmountsPopover
                    .cellEditorParams,
                  maxEntries: 10,
                },
              },
              salaryMultiplier: sharedCellSpecs.salaryMultiplier,
              multipleSalaryMultipliers:
                sharedCellSpecs.multipleSalaryMultipliersPopover,
            },
            default: sharedCellSpecs.dollarAmount,
          },
        },
        spouseBenefitDescriptionMinimum: {
          label: "Minimum",
          description: "Minimum spouse benefit amount",
          cell: sharedCellSpecs.dollarAmount,
          networkDependent: false,
          nested: true,
          editableWhen: {
            dependsOn: "spouseBenefitDescriptionType",
            disabledValues: [
              "notIncluded",
              "multipleOptions",
              "multipleDollarAmounts",
              "multipleSalaryMultipliers",
              NotDisclosed,
            ],
          },
        },
      },
    ],
    "dependentBenefitsIncluded",
    ["no"],
  ),
  ...configureDependentBenefitFields(
    [
      {
        spouseBenefitMaximumType: {
          label: "Benefit Maximum - Spouse",
          description: "Type of spouse benefit maximum",
          cell: {
            ...sharedCellSpecs.enumDropdown,
            cellEditorParams: {
              ...sharedCellSpecs.enumDropdown.cellEditorParams,
              values: extendEnumWithNotDisclosed(
                RfpCriticalIllnessSpouseMaximumTypeEnum,
              ),
            },
          },
          networkDependent: false,
          isRequired: true,
          refreshesCells: [
            "spouseBenefitMaximumDollarAmount",
            "spouseBenefitMaximumPercent",
          ],
        },
        spouseBenefitMaximumDollarAmount: {
          label: "Dollar Amount",
          description: "Maximum amount in dollars",
          cell: sharedCellSpecs.dollarAmount,
          networkDependent: false,
          nested: true,
          editableWhen: {
            dependsOn: "spouseBenefitMaximumType",
            disabledValues: [
              "notIncluded",
              "percentageOfEeBenefit",
              NotDisclosed,
            ],
          },
        },
        spouseBenefitMaximumPercent: {
          label: "Percentage",
          description: "Maximum amount as percentage",
          cell: sharedCellSpecs.percentageAmount,
          networkDependent: false,
          nested: true,
          editableWhen: {
            dependsOn: "spouseBenefitMaximumType",
            disabledValues: ["notIncluded", "dollarAmount", NotDisclosed],
          },
        },
      },
    ],
    "dependentBenefitsIncluded",
    ["no"],
  ),
  ...configureDependentBenefitFields(
    [
      buildTypeAmountWithVariantsBenefit({
        prefix: "spouseGuaranteeIssue",
        typeLabel: "Guarantee Issue - Spouse",
        typeCell: sharedCellSpecs.enumDropdown,
        typeEnumValues: extendEnumWithNotDisclosed(
          RfpCriticalIllnessEmployeeGuaranteeIssueTypeEnum,
        ),
        amountLabel: "Amount",
        typeDescription: "Type of spouse guarantee issue",
        amountDescription: "Spouse guarantee issue amount",
        cellSpecVariants: {
          percentageOfEeBenefit: sharedCellSpecs.percentageAmount,
          flatAmount: sharedCellSpecs.dollarAmount,
        },
        defaultCellSpec: sharedCellSpecs.dollarAmount,
        disabledValues: ["notIncluded", NotDisclosed],
        isRequired: true,
      }),
    ],
    "dependentBenefitsIncluded",
    ["no"],
  ),
  ...configureDependentBenefitFields(
    [
      {
        childBenefitDescriptionType: {
          label: "Benefit Description - Child",
          description: "Type of child benefit description",
          cell: {
            ...sharedCellSpecs.enumDropdown,
            cellEditorParams: {
              ...sharedCellSpecs.enumDropdown.cellEditorParams,
              values: Object.values(
                RfpCriticalIllnessEmployeeGuaranteeIssueTypeEnum,
              ),
            },
          },
          networkDependent: false,
          isRequired: true,
          refreshesCells: [
            "childBenefitDescriptionAmount",
            "childBenefitDescriptionMinimum",
          ],
        },
        childBenefitDescriptionAmount: {
          label: "Amount",
          description: "Child benefit amount",
          cell: sharedCellSpecs.dollarAmount,
          networkDependent: false,
          nested: true,
          editableWhen: {
            dependsOn: "childBenefitDescriptionType",
            disabledValues: ["notIncluded", NotDisclosed],
          },
          cellVariants: {
            dependsOn: "childBenefitDescriptionType",
            options: {
              percentageOfEeBenefit: sharedCellSpecs.percentageAmount,
              flatAmount: {
                ...sharedCellSpecs.multipleDollarAmountsPopover,
                cellEditorParams: {
                  ...sharedCellSpecs.multipleDollarAmountsPopover
                    .cellEditorParams,
                  minEntries: 1,
                  maxEntries: 2,
                },
              },
            },
            default: sharedCellSpecs.dollarAmount,
          },
        },
        childBenefitDescriptionMinimum: {
          label: "Minimum",
          description: "Minimum child benefit amount",
          cell: sharedCellSpecs.dollarAmount,
          networkDependent: false,
          nested: true,
          editableWhen: {
            dependsOn: "childBenefitDescriptionType",
            disabledValues: ["notIncluded", NotDisclosed],
          },
        },
      },
    ],
    "dependentBenefitsIncluded",
    ["no"],
  ),
  ...buildTypeAmountBenefitPair({
    prefix: "invasiveCancer",
    typeLabel: "Invasive Cancer",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpIllnessTypeEnum),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Coverage type for invasive cancer",
    amountDescription: "Invasive cancer coverage percentage",
    disabledValues: ["notIncluded", NotDisclosed],
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "nonInvasiveCancer",
    typeLabel: "Non-Invasive Cancer",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpIllnessTypeEnum),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Coverage type for non-invasive cancer",
    amountDescription: "Non-invasive cancer coverage percentage",
    disabledValues: ["notIncluded", NotDisclosed],
    isRequired: true,
  }),
  // Extended (non-v5) field (CD-390/CD-2074). Backend field is a plain
  // string for now, not yet an enum, so this is a text field rather than a
  // dropdown. Skin Cancer's row in the source spreadsheet is still "Pending
  // Approval" (open question about adding a Dollar Amount option) — shipped
  // with the approved value list anyway since it matches Advanced
  // Parkinson's/ALS; revisit if that question resolves differently
  // (CD-2325).
  ...buildMpOnlyConditionRider({
    prefix: "skinCancer",
    typeLabel: "Skin Cancer",
    typeDescription: "Skin cancer benefit type",
    amountDescription: "Skin cancer benefit amount",
    mpConfigKey: "skin_cancer",
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "category1HeartAttack",
    typeLabel: "Category 1 - Heart Attack",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpIllnessTypeEnum),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Coverage type for Category 1 heart attack",
    amountDescription: "Category 1 heart attack coverage percentage",
    disabledValues: ["notIncluded", NotDisclosed],
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "category1Stroke",
    typeLabel: "Category 1 - Stroke",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpIllnessTypeEnum),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Coverage type for Category 1 stroke",
    amountDescription: "Category 1 stroke coverage percentage",
    disabledValues: ["notIncluded", NotDisclosed],
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "kidneyFailure",
    typeLabel: "Kidney Failure",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(RfpIllnessTypeEnum),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.percentageAmount,
    typeDescription: "Coverage type for kidney failure",
    amountDescription: "Kidney failure coverage percentage",
    disabledValues: ["notIncluded", NotDisclosed],
    isRequired: true,
  }),
  // Extended (non-v5) fields (CD-390/CD-2074). Backend fields are plain
  // strings for now, not yet enums, so these are text fields rather than
  // dropdowns.
  // Advanced Parkinson's and ALS are type+amount pairs, not a single
  // dropdown: the product decision (CD-2325) is a percentage or dollar
  // amount alongside the usual sentinels, so each condition needs an amount
  // field that unlocks when a numeric type is selected. The popup editor
  // and row-collapsing come from their entries in
  // `criticalIllnessTypedAmountFields` (@platform/formatting-utils); the
  // cellSpecVariants/defaultCellSpec here just keep the raw cell specs
  // correct for non-interactive consumers (e.g. CSV export).
  ...buildMpOnlyConditionRider({
    prefix: "advancedParkinsons",
    typeLabel: "Advanced Parkinson's",
    typeDescription: "Advanced Parkinson's benefit type",
    amountDescription: "Advanced Parkinson's benefit amount",
    mpConfigKey: "advanced_parkinsons",
  }),
  ...buildMpOnlyConditionRider({
    prefix: "als",
    typeLabel: "ALS",
    typeDescription: "ALS benefit type",
    amountDescription: "ALS benefit amount",
    mpConfigKey: "als",
  }),
  maximumPayoutType: {
    label: "Maximum Payout",
    description: "Type of maximum payout limit",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpMaximumPayoutTypeEnum,
        ),
      },
    },
    networkDependent: false,
    isRequired: true,
    refreshesCells: ["maximumPayoutAmount"],
  },
  maximumPayoutAmount: {
    label: "Amount",
    description: "Maximum payout amount",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
    nested: true,
    editableWhen: {
      dependsOn: "maximumPayoutType",
      disabledValues: [
        "notIncluded",
        "noLimit",
        "unlimited",
        "willNotPayForThirdOrLaterOccurrence",
        NotDisclosed,
        NotOffered,
      ],
    },
    cellVariants: {
      dependsOn: "maximumPayoutType",
      options: {
        percentage: sharedCellSpecs.percentageAmountHighRange,
        dollarAmount: sharedCellSpecs.dollarAmount,
      },
      default: sharedCellSpecs.dollarAmount,
    },
  },
  ...buildTypeAmountBenefitPair({
    prefix: "occurrenceOfDifferentIllness",
    typeLabel: "Occurrence of Different Illness",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpOccurrenceTypeEnum,
    ),
    amountLabel: "Months",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Coverage for different illness occurrence",
    amountDescription: "Waiting period in months for different illness",
    valueSuffix: "Months",
    disabledValues: ["included", "notIncluded", NotDisclosed, NotOffered],
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "additionalOccurrenceOfSameIllness",
    typeLabel: "Additional Occurrence of Same Illness",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpOccurrenceTypeEnum,
    ),
    amountLabel: "Months",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Coverage for same illness recurrence",
    amountDescription: "Waiting period in months for same illness recurrence",
    amountDefaultValue: 0,
    valueSuffix: "Months",
    disabledValues: ["included", "notIncluded", NotDisclosed, NotOffered],
  }),
  ...buildAgeReductionScheduleBenefitPair({
    isRequired: true,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpAgeReductionScheduleType,
    ),
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
  }),
  ...buildPreExistingConditionBenefitPair({
    typeDescription: "Type of pre-existing condition limitation",
    monthsDetailDescription:
      "Configure months diagnosis free before/after effective date and treatment free. Format: XX/XX or XX/XX/XX",
    disabledValues: ["waived", "none", NotDisclosed],
    monthsDetailDefaultValue: "01/01",
    isRequired: true,
  }),
  portability: {
    label: "Portability",
    description:
      "Whether policy holder can continue policy after separation of employment",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(RfpInclusionStatusEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "wellnessBenefit",
    typeLabel: "Wellness Benefit",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: ["dollarAmount", "notIncluded", NotDisclosed, NotOffered],
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Type of wellness benefit reimbursement",
    amountDescription: "Wellness benefit dollar amount",
    disabledValues: ["notIncluded", NotDisclosed, NotOffered],
    isRequired: true,
  }),
  // Unlike its sibling extended fields, eligibilityHours has a
  // confirmed dropdown value list (CD-2325).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Whether annual open enrollment is included in the plan",
    cell: sharedCellSpecs.annualOpenEnrollmentType,
    networkDependent: false,
    isRequired: true,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  volumeIllustratedFrom: mpOnlyRow(
    "Volume illustrated from _",
    "volume_illustrated_from",
  ),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),
  ageBasis: {
    label: "Age Basis",
    description: "Whether benefits are based on issue age or attained age",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: Object.values(RfpAgeBasisEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
  },
};
